import { addDays, daysInYear, eachDate, formatDisplayDate, inclusiveDays, isoDate, weekday, yearOf } from './dates'
import { isPublicHoliday } from './payCycle'
import type { DayPortion, LeaveBalance, LeaveEmployee, LeaveRequest, LeaveType } from './types'

export const SICK_LEAVE_SERVICE_DAYS = 13 * 7
export const DEFAULT_WORKING_DAYS = [1, 2, 3, 4, 5]
export const DEFAULT_HOURS_PER_DAY = 7.5

function sortWeekdays(days: number[]): number[] {
  return [...days].sort((left, right) => ((left + 6) % 7) - ((right + 6) % 7))
}

export function normalizeWorkingDays(value: unknown): number[] {
  if (!Array.isArray(value)) return [...DEFAULT_WORKING_DAYS]
  const days = sortWeekdays([...new Set(value.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))])
  return days.length ? days : [...DEFAULT_WORKING_DAYS]
}

export function requireWorkingDays(value: unknown): number[] {
  if (!Array.isArray(value)) throw new Error('Choose at least one working day')
  const days = sortWeekdays([...new Set(value.map(Number).filter((day) => Number.isInteger(day) && day >= 0 && day <= 6))])
  if (!days.length) throw new Error('Choose at least one working day')
  return days
}

function roundHours(value: number): number {
  return Math.round(value * 100) / 100
}

export function hoursPerDayOf(employee: Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours'>): number {
  const daily = employee.dailyHours
  if (daily && Object.keys(daily).length) {
    const values = Object.values(daily)
    if (values.every((hours) => hours === values[0])) return values[0]
  }
  if (typeof employee.hoursPerDay === 'number' && employee.hoursPerDay > 0) return employee.hoursPerDay
  return employee.regularWeeklyHours / normalizeWorkingDays(employee.workingDays).length
}

export function dailyHoursOf(employee: Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours'>): Record<string, number> {
  if (employee.dailyHours && Object.keys(employee.dailyHours).length) {
    return { ...employee.dailyHours }
  }
  const rate = roundHours(hoursPerDayOf(employee))
  const dailyHours: Record<string, number> = {}
  normalizeWorkingDays(employee.workingDays).forEach((day) => {
    dailyHours[String(day)] = rate
  })
  return dailyHours
}

export function hoursOnDate(employee: Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours'>, date: string): number {
  const specific = employee.dailyHours?.[String(weekday(date))]
  if (typeof specific === 'number' && specific > 0) return specific
  return hoursPerDayOf(employee)
}

type HoursEmployee = Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours' | 'annualLeaveWeeks'> & { annualLeaveDays?: number }

export function fullDayHours(employee: HoursEmployee): number {
  const daily = employee.dailyHours
  if (daily && Object.keys(daily).length) {
    const values = Object.values(daily).filter((hours) => hours > 0)
    if (values.length) return Math.max(...values)
  }
  return hoursPerDayOf(employee)
}

export function dayEquivalentsPerWeek(employee: HoursEmployee): number {
  const daily = employee.dailyHours
  if (daily && Object.keys(daily).length) {
    const full = fullDayHours(employee)
    if (!(full > 0)) return 0
    return Object.values(daily).filter((hours) => hours > 0).reduce((sum, hours) => sum + hours / full, 0)
  }
  return normalizeWorkingDays(employee.workingDays).length
}

export function annualLeaveWeeksOf(employee: HoursEmployee): number {
  if (typeof employee.annualLeaveWeeks === 'number' && employee.annualLeaveWeeks >= 0) return employee.annualLeaveWeeks
  const equivalents = dayEquivalentsPerWeek(employee)
  if (equivalents > 0 && typeof employee.annualLeaveDays === 'number') return employee.annualLeaveDays / equivalents
  return 4
}

export function contractualAnnualDays(employee: HoursEmployee): number {
  return annualLeaveWeeksOf(employee) * dayEquivalentsPerWeek(employee)
}

export function annualDayWeight(employee: HoursEmployee, date: string, portion: number): number {
  const full = fullDayHours(employee)
  if (!(full > 0)) return portion
  return portion * (hoursOnDate(employee, date) / full)
}

export function annualRequestDays(
  employee: HoursEmployee,
  request: { startDate: string; endDate: string; startPortion: DayPortion; endPortion: DayPortion },
): number {
  return expandLeaveDays(request, employee.workingDays).reduce((sum, day) => sum + annualDayWeight(employee, day.date, day.portion), 0)
}

export function hoursForLeaveDays(
  employee: Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours'>,
  days: Array<{ date: string; portion: number }>,
): number {
  return days.reduce((sum, day) => sum + day.portion * hoursOnDate(employee, day.date), 0)
}

export function workingPatternFromInput(payload: {
  workingDays?: unknown
  hoursPerDay?: unknown
  regularWeeklyHours?: unknown
  dailyHours?: unknown
}): { workingDays: number[]; hoursPerDay?: number; dailyHours: Record<string, number>; regularWeeklyHours: number } {
  if (payload.dailyHours && typeof payload.dailyHours === 'object' && !Array.isArray(payload.dailyHours)) {
    const dailyHours: Record<string, number> = {}
    Object.entries(payload.dailyHours as Record<string, unknown>).forEach(([key, value]) => {
      const day = Number(key)
      if (!Number.isInteger(day) || day < 0 || day > 6) return
      if (value === '' || value === null || value === undefined) return
      const hours = Number(value)
      if (!Number.isFinite(hours) || hours === 0) return
      if (hours < 0.5 || hours > 24) {
        throw new Error('Hours for a working day must be between 0.5 and 24. Use 0 for a day they do not work.')
      }
      dailyHours[String(day)] = roundHours(hours)
    })
    const workingDays = sortWeekdays(Object.keys(dailyHours).map(Number))
    if (!workingDays.length) throw new Error('Choose at least one working day')
    const rates = workingDays.map((day) => dailyHours[String(day)])
    return {
      workingDays,
      dailyHours,
      hoursPerDay: rates.every((hours) => hours === rates[0]) ? rates[0] : undefined,
      regularWeeklyHours: roundHours(rates.reduce((sum, hours) => sum + hours, 0)),
    }
  }
  const hasDays = Array.isArray(payload.workingDays)
  const hasHoursPerDay = payload.hoursPerDay !== undefined && payload.hoursPerDay !== '' && payload.hoursPerDay !== null
  const workingDays = hasDays ? requireWorkingDays(payload.workingDays) : [...DEFAULT_WORKING_DAYS]
  let hoursPerDay = Number(payload.hoursPerDay)
  if (!hasHoursPerDay) {
    const weekly = Number(payload.regularWeeklyHours)
    if (!Number.isFinite(weekly) || weekly <= 0) throw new Error('Hours per day must be greater than zero')
    hoursPerDay = weekly / workingDays.length
  }
  if (!Number.isFinite(hoursPerDay) || hoursPerDay < 0.5 || hoursPerDay > 24) {
    throw new Error('Hours per day must be between 0.5 and 24')
  }
  const rate = roundHours(hoursPerDay)
  const dailyHours: Record<string, number> = {}
  workingDays.forEach((day) => {
    dailyHours[String(day)] = rate
  })
  return {
    workingDays,
    hoursPerDay: rate,
    dailyHours,
    regularWeeklyHours: roundHours(rate * workingDays.length),
  }
}

export interface LeaveDay {
  date: string
  portion: DayPortion
}

export function sickEligibleFrom(startDate: string): string {
  return addDays(startDate, SICK_LEAVE_SERVICE_DAYS)
}

export function studyLeaveAllowance(employee: { studyLeaveDays?: number }): number {
  const days = Number(employee.studyLeaveDays)
  if (!Number.isFinite(days) || days < 0) return 0
  return days
}

function anniversaryOn(startDate: string, year: number): string {
  const month = Number(startDate.slice(5, 7))
  const day = Number(startDate.slice(8, 10))
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate()
  return isoDate(year, month, Math.min(day, last))
}

/** The study-leave year runs from the contract start date to the day before the next anniversary. */
export function studyContractYear(startDate: string, asOf: string): { start: string; end: string } | null {
  if (!startDate || asOf < startDate) return null
  const year = yearOf(asOf)
  const thisAnniversary = anniversaryOn(startDate, year)
  const start = asOf >= thisAnniversary ? thisAnniversary : anniversaryOn(startDate, year - 1)
  return { start, end: addDays(anniversaryOn(startDate, yearOf(start) + 1), -1) }
}

function studyDaysUsed(
  employeeId: string,
  requests: LeaveRequest[],
  year: { start: string; end: string },
  workingDays: number[] | undefined,
  ignoreRequestId?: string,
): number {
  let used = 0
  requests.forEach((request) => {
    if (request.id === ignoreRequestId) return
    if (request.employeeId !== employeeId || request.type !== 'study' || request.status !== 'approved') return
    expandLeaveDays(request, workingDays).forEach((day) => {
      if (day.date >= year.start && day.date <= year.end) used += day.portion
    })
  })
  return used
}

function portionForWorkingDay(
  date: string,
  request: { startDate: string; endDate: string; startPortion: DayPortion; endPortion: DayPortion },
  isOnlyDay: boolean,
): DayPortion {
  if (isOnlyDay) {
    if (date === request.startDate) return request.startPortion
    if (date === request.endDate) return request.endPortion
    return 1
  }
  if (date === request.startDate) return request.startPortion
  if (date === request.endDate) return request.endPortion
  return 1
}

export function expandLeaveDays(request: {
  startDate: string
  endDate: string
  startPortion: DayPortion
  endPortion: DayPortion
}, workingDays?: number[]): LeaveDay[] {
  if (request.endDate < request.startDate) {
    throw new Error('The end date must be on or after the start date')
  }
  if (yearOf(request.startDate) !== yearOf(request.endDate)) {
    throw new Error('Leave cannot be carried into the next year')
  }
  const allowed = new Set(normalizeWorkingDays(workingDays))
  const dates = eachDate(request.startDate, request.endDate).filter((date) => allowed.has(weekday(date)) && !isPublicHoliday(date))
  return dates.map((date) => ({
    date,
    portion: portionForWorkingDay(date, request, dates.length === 1),
  }))
}

export function requestDays(request: {
  startDate: string
  endDate: string
  startPortion: DayPortion
  endPortion: DayPortion
}, workingDays?: number[]): number {
  return expandLeaveDays(request, workingDays).reduce((sum, day) => sum + day.portion, 0)
}

export function leaveDaysOverlap(
  left: { startDate: string; endDate: string; startPortion: DayPortion; endPortion: DayPortion },
  right: { startDate: string; endDate: string; startPortion: DayPortion; endPortion: DayPortion },
  workingDays?: number[],
): boolean {
  const rightDates = new Set(expandLeaveDays(right, workingDays).map((day) => day.date))
  return expandLeaveDays(left, workingDays).some((day) => rightDates.has(day.date))
}

export function rangesOverlap(
  left: { startDate: string; endDate: string },
  right: { startDate: string; endDate: string },
): boolean {
  return left.startDate <= right.endDate && right.startDate <= left.endDate
}

export function accruedAnnualDays(employee: HoursEmployee & Pick<LeaveEmployee, 'startDate'>, asOf: string): number {
  if (asOf < employee.startDate) return 0
  const year = yearOf(asOf)
  const yearStart = `${year}-01-01`
  const yearEnd = `${year}-12-31`
  const from = employee.startDate > yearStart ? employee.startDate : yearStart
  if (asOf < from) return 0
  const to = asOf < yearEnd ? asOf : yearEnd
  return contractualAnnualDays(employee) * (inclusiveDays(from, to) / daysInYear(year))
}

function approvedDays(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  type: LeaveType,
  year: number,
  predicate: (date: string) => boolean,
): number {
  let used = 0
  requests.forEach((request) => {
    if (request.employeeId !== employee.id || request.type !== type || request.status !== 'approved') return
    if (yearOf(request.startDate) !== year) return
    expandLeaveDays(request, employee.workingDays).forEach((day) => {
      if (!predicate(day.date)) return
      used += type === 'annual' ? annualDayWeight(employee, day.date, day.portion) : day.portion
    })
  })
  return used
}

function approvedDaysAfterOverride(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  type: LeaveType,
  overrideAt: string,
  ignoreRequestId?: string,
): number {
  let used = 0
  requests.forEach((request) => {
    if (request.id === ignoreRequestId) return
    if (request.employeeId !== employee.id || request.type !== type || request.status !== 'approved') return
    if (!request.createdAt || request.createdAt <= overrideAt) return
    used += type === 'annual' ? annualRequestDays(employee, request) : requestDays(request, employee.workingDays)
  })
  return used
}

function dublinDate(isoTimestamp: string): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Dublin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(isoTimestamp))
}

function annualOverrideApplies(
  employee: Pick<LeaveEmployee, 'annualBalanceOverride' | 'annualBalanceOverrideAt' | 'annualBalanceOverrideAsOf'>,
  asOf: string,
): boolean {
  if (typeof employee.annualBalanceOverride !== 'number' || !employee.annualBalanceOverrideAt) return false
  const setOn = employee.annualBalanceOverrideAsOf || dublinDate(employee.annualBalanceOverrideAt)
  return yearOf(asOf) === yearOf(setOn) && asOf >= setOn
}

export function annualAvailableDays(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  asOf: string,
  ignoreRequestId?: string,
): number {
  if (annualOverrideApplies(employee, asOf) && employee.annualBalanceOverrideAt) {
    const setOn = employee.annualBalanceOverrideAsOf || dublinDate(employee.annualBalanceOverrideAt)
    const furtherAccrual = employee.annualAccrualFrozen
      ? 0
      : accruedAnnualDays(employee, asOf) - accruedAnnualDays(employee, setOn)
    return employee.annualBalanceOverride! + furtherAccrual - approvedDaysAfterOverride(
      employee,
      requests,
      'annual',
      employee.annualBalanceOverrideAt,
      ignoreRequestId,
    )
  }
  const year = yearOf(asOf)
  const others = ignoreRequestId ? requests.filter((request) => request.id !== ignoreRequestId) : requests
  const committed = approvedDays(employee, others, 'annual', year, () => true)
  return accruedAnnualDays(employee, asOf) - committed
}

export function leaveBalance(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  asOf: string,
  statutorySickDays: number,
): LeaveBalance {
  const year = yearOf(asOf)
  const eligibleFrom = sickEligibleFrom(employee.startDate)
  const sickEligible = asOf >= eligibleFrom
  const workingDays = employee.workingDays
  const annualAccrued = accruedAnnualDays(employee, asOf)
  const annualTaken = approvedDays(employee, requests, 'annual', year, (date) => date <= asOf)
  const annualBooked = approvedDays(employee, requests, 'annual', year, (date) => date > asOf)
  const sickUsed = approvedDays(employee, requests, 'sick', year, () => true)
  const annualBalanceSet = annualOverrideApplies(employee, asOf)
  const annualAccrualFrozen = annualBalanceSet && employee.annualAccrualFrozen === true
  const sickBalanceSet = typeof employee.sickBalanceOverride === 'number' && !!employee.sickBalanceOverrideAt
  const sickEntitlement = sickBalanceSet ? employee.sickBalanceOverride! : (sickEligible ? statutorySickDays : 0)
  const annualAvailable = annualBalanceSet
    ? Math.max(0, annualAvailableDays(employee, requests, asOf))
    : Math.max(0, annualAccrued - annualTaken - annualBooked)
  const sickAvailable = sickBalanceSet
    ? Math.max(0, employee.sickBalanceOverride! - approvedDaysAfterOverride(employee, requests, 'sick', employee.sickBalanceOverrideAt!))
    : Math.max(0, sickEntitlement - sickUsed)
  const contractYear = studyContractYear(employee.startDate, asOf)
  const studyEntitlement = contractYear ? studyLeaveAllowance(employee) : 0
  const studyUsed = contractYear ? studyDaysUsed(employee.id, requests, contractYear, workingDays) : 0
  const forthcoming = studyContractYear(employee.startDate, employee.startDate)
  return {
    asOf,
    annualAccrued,
    annualTaken,
    annualBooked,
    annualAvailable,
    annualBalanceSet,
    annualAccrualFrozen,
    sickEligible: sickBalanceSet || sickEligible,
    sickEligibleFrom: eligibleFrom,
    sickEntitlement,
    sickUsed,
    sickAvailable,
    sickBalanceSet,
    studyEntitlement,
    studyUsed,
    studyAvailable: Math.max(0, studyEntitlement - studyUsed),
    studyYearStart: contractYear?.start || forthcoming?.start || employee.startDate,
    studyYearEnd: contractYear?.end || forthcoming?.end || employee.startDate,
  }
}

type AnnualDraft = Pick<LeaveRequest, 'startDate' | 'endDate' | 'startPortion' | 'endPortion' | 'type'> & { id?: string }

function pendingAnnualDays(employee: LeaveEmployee, requests: LeaveRequest[], year: number, ignoreRequestId?: string): number {
  let reserved = 0
  requests.forEach((request) => {
    if (ignoreRequestId && request.id === ignoreRequestId) return
    if (request.employeeId !== employee.id || request.type !== 'annual' || request.status !== 'pending') return
    if (yearOf(request.startDate) !== year) return
    reserved += annualRequestDays(employee, request)
  })
  return reserved
}

export function annualLeaveAvailableForRequest(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  request: AnnualDraft,
): number {
  const atStart = annualAvailableDays(employee, requests, request.startDate, request.id)
  return atStart - pendingAnnualDays(employee, requests, yearOf(request.startDate), request.id)
}

export function annualShortfallMessage(requested: number, available: number): string {
  const days = (value: number) => (Math.round(value * 100) / 100).toFixed(2)
  return `You do not have enough leave for this period. This request is ${days(requested)} days and ${days(Math.max(0, available))} days are available.`
}

export function annualRequestExceedsAccrual(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  request: AnnualDraft,
): boolean {
  if (request.type !== 'annual') return false
  return annualRequestDays(employee, request) > annualLeaveAvailableForRequest(employee, requests, request) + 1e-9
}

export function assertAnnualAllowed(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  request: AnnualDraft,
): void {
  if (!annualRequestExceedsAccrual(employee, requests, request)) return
  throw new Error(annualShortfallMessage(
    annualRequestDays(employee, request),
    annualLeaveAvailableForRequest(employee, requests, request),
  ))
}

export function assertSickAllowed(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  request: Pick<LeaveRequest, 'id' | 'startDate' | 'endDate' | 'startPortion' | 'endPortion'>,
  statutorySickDays: number,
): void {
  if (typeof employee.sickBalanceOverride === 'number' && employee.sickBalanceOverrideAt) {
    const used = approvedDaysAfterOverride(employee, requests, 'sick', employee.sickBalanceOverrideAt, request.id)
    if (requestDays(request, employee.workingDays) > employee.sickBalanceOverride - used + 1e-9) {
      throw new Error('This is more than the remaining sick leave')
    }
    return
  }
  const eligibleFrom = sickEligibleFrom(employee.startDate)
  if (request.startDate < eligibleFrom) {
    throw new Error('Statutory sick leave is available after 13 weeks of service')
  }
  const year = yearOf(request.startDate)
  const others = requests.filter((item) => item.id !== request.id)
  const used = approvedDays(employee, others, 'sick', year, () => true)
  const remaining = statutorySickDays - used
  if (requestDays(request, employee.workingDays) > remaining + 1e-9) {
    throw new Error('This is more than the remaining statutory sick leave')
  }
}

export function assertStudyAllowed(
  employee: LeaveEmployee,
  requests: LeaveRequest[],
  request: Pick<LeaveRequest, 'id' | 'startDate' | 'endDate' | 'startPortion' | 'endPortion'>,
): void {
  if (request.startDate < employee.startDate) {
    throw new Error('Study leave is available from the contract start date')
  }
  const year = studyContractYear(employee.startDate, request.startDate)
  if (!year) throw new Error('Study leave is available from the contract start date')
  const days = expandLeaveDays(request, employee.workingDays)
  if (days.some((day) => day.date < year.start || day.date > year.end)) {
    throw new Error(`Study leave cannot cross the contract anniversary on ${formatDisplayDate(addDays(year.end, 1))}. Request those periods separately.`)
  }
  const used = studyDaysUsed(employee.id, requests, year, employee.workingDays, request.id)
  if (days.reduce((sum, day) => sum + day.portion, 0) > studyLeaveAllowance(employee) - used + 1e-9) {
    throw new Error('This is more than the remaining study leave')
  }
}

export function hoursForDays(employee: Pick<LeaveEmployee, 'hoursPerDay' | 'regularWeeklyHours' | 'workingDays' | 'dailyHours'>, days: number): number {
  return hoursPerDayOf(employee) * days
}

export function canCancelRequest(request: Pick<LeaveRequest, 'status' | 'startDate'>, asOf: string): boolean {
  if (request.status === 'pending') return true
  return request.status === 'approved' && request.startDate > asOf
}
